/**
 * 持っている店を全部まとめて見る画面。
 *
 * なぜ要るか:
 *   個人で数店やっているオーナーが、他店の様子を見るのに
 *   店を切り替えて回るしかなかった。
 *   「今夜どこが動いているか」を知りたいだけなのに、
 *   3店なら3回切り替えて3回読み込みを待つことになる。
 *
 *   アカウントを分ける必要は無い。1つのアカウントに
 *   複数店の権限が付く仕組みは元からあった（shopAccess）。
 *   足りなかったのは、それを並べて見せる画面だけ。
 *
 * 数え方:
 *   伝票は businessDate（'YYYY-MM-DD'）で数える。
 *   会計のときに営業日の区切りを当てたうえで作られている値なので、
 *   ここで区切りを当て直さない（二重に当てると1日ずれる）。
 *   料金画面と同じ数え方にしてある。片方だけ変えると
 *   「一覧では 300 件なのに料金画面では 280 件」になる。
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Divider,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { taxRatePercentLabel } from '../utils/taxRate';

interface Row {
  shopId: string;
  shopName: string;
  role: string;
  /** 今夜の売上（税込・会計に記録された合計） */
  tonightSales: number;
  tonightSlips: number;
  monthSales: number;
  monthSlips: number;
  error: string | null;
}

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

const ROLE_LABEL: Record<string, string> = {
  owner: 'オーナー', manager: '店長', staff: 'スタッフ',
};

const AllShops: React.FC = () => {
  const { availableShops } = useAuth() as any;
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ids: string[] = (availableShops ?? []).map((a: any) => a.shopId);
    if (ids.length === 0) { setLoading(false); return; }

    (async () => {
      try {
        setLoading(true);

        // 店の名前をまとめて引く。in は10件までなので分けて投げる。
        const names = new Map<string, string>();
        for (let i = 0; i < ids.length; i += 10) {
          const snap = await getDocs(query(
            collection(db, 'shops'), where(documentId(), 'in', ids.slice(i, i + 10))));
          snap.docs.forEach((d) => names.set(d.id, (d.data() as any).shopName ?? d.id));
        }

        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
        const monthFrom = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
        const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const monthTo = `${nextMonth.getFullYear()}-${pad(nextMonth.getMonth() + 1)}-01`;

        const out: Row[] = [];
        for (const a of (availableShops ?? [])) {
          const shopId = a.shopId as string;
          const row: Row = {
            shopId, shopName: names.get(shopId) ?? shopId, role: a.role,
            tonightSales: 0, tonightSlips: 0, monthSales: 0, monthSlips: 0, error: null,
          };
          try {
            const snap = await getDocs(query(
              collection(db, 'checkouts'),
              where('shopId', '==', shopId),
              where('businessDate', '>=', monthFrom),
              where('businessDate', '<', monthTo),
            ));
            snap.docs.forEach((d) => {
              const c = d.data() as any;
              // 取り消した伝票は売上に入れない
              
              if (c.status === 'voided' || c.status === 'cancelled') return;
              const total = Number(c.total) || 0;
              row.monthSales += total;
              row.monthSlips += 1;
              if (c.businessDate === today) {
                row.tonightSales += total;
                row.tonightSlips += 1;
              }
            });
          } catch (e: any) {
            // 1店読めなくても他店は出す。黙って0にしない。
            row.error = e?.message ?? '読めませんでした';
          }
          out.push(row);
        }
        // 今夜の売上が多い順。動いている店から見たい。
        out.sort((a, b) => b.tonightSales - a.tonightSales);
        setRows(out);
      } catch (e: any) {
        setError(e?.message ?? '読み込めませんでした');
      } finally {
        setLoading(false);
      }
    })();
  }, [availableShops]);

  const total = useMemo(() => ({
    tonightSales: rows.reduce((s, r) => s + r.tonightSales, 0),
    tonightSlips: rows.reduce((s, r) => s + r.tonightSlips, 0),
    monthSales: rows.reduce((s, r) => s + r.monthSales, 0),
    monthSlips: rows.reduce((s, r) => s + r.monthSlips, 0),
  }), [rows]);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>;

  if ((availableShops ?? []).length <= 1) {
    return (
      <Box sx={{ p: 3, maxWidth: 900 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>店舗の一覧</Typography>
        <Alert severity="info">
          いまのアカウントで見られる店は1つだけです。
          2店目からは、この画面で全店の売上を並べて見られます。
          店を増やすときは運営にご連絡ください（アカウントは1つのままです）。
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>店舗の一覧</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        このアカウントで見られる {rows.length} 店の売上です。
        店を切り替えなくても、ここで並べて確かめられます。
        金額は税込（{taxRatePercentLabel(null)} を含む会計の合計）です。
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">今夜の合計</Typography>
            <Typography variant="h4">{yen(total.tonightSales)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {total.tonightSlips.toLocaleString()} 伝票
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">今月の合計</Typography>
            <Typography variant="h4">{yen(total.monthSales)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {total.monthSlips.toLocaleString()} 伝票
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>店</TableCell>
                <TableCell align="right">今夜の売上</TableCell>
                <TableCell align="right">今夜の伝票</TableCell>
                <TableCell align="right">今月の売上</TableCell>
                <TableCell align="right">今月の伝票</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.shopId}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StoreIcon fontSize="small" color="disabled" />
                      <span>{r.shopName}</span>
                      <Chip size="small" label={ROLE_LABEL[r.role] ?? r.role} />
                    </Stack>
                    {r.error && (
                      <Typography variant="caption" color="error">
                        この店だけ読めませんでした: {r.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.error ? '—' : yen(r.tonightSales)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.error ? '—' : r.tonightSlips.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.error ? '—' : yen(r.monthSales)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {r.error ? '—' : r.monthSlips.toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            今夜の売上が多い順に並べています。
            伝票の数え方は「ご利用プラン」の画面と同じです。
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AllShops;
